import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, getRole, logout, getUserId } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./CourseListPage.css";

const CARD_COLORS = [
  { accent: "#3b82f6", bg: "rgba(59,130,246,0.08)",  text: "#3b82f6" },
  { accent: "#6366f1", bg: "rgba(99,102,241,0.08)",  text: "#6366f1" },
  { accent: "#22c55e", bg: "rgba(34,197,94,0.08)",   text: "#22c55e" },
  { accent: "#f59e0b", bg: "rgba(245,158,11,0.08)",  text: "#f59e0b" },
  { accent: "#ec4899", bg: "rgba(236,72,153,0.08)",  text: "#ec4899" },
  { accent: "#14b8a6", bg: "rgba(20,184,166,0.08)",  text: "#14b8a6" },
];

export default function CourseListPage() {
  const [courses, setCourses]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [search, setSearch]           = useState("");
  const [dialogMode, setDialogMode]   = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [name, setName]               = useState("");
  const [description, setDescription] = useState("");

  const navigate     = useNavigate();
  const role         = getRole();
  const isManagement = role === "admin" || role === "subadmin";

  /* ── FETCH ── */
  const fetchCourses = useCallback(async () => {
    setPageLoading(true);
    const t0 = Date.now();
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/courses/student`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ studentId: getUserId() }),
      });
      if (res.status === 401 || res.status === 403) { logout(); navigate("/login"); return; }
      setCourses((await res.json()) || []);
    } catch (err) { console.error(err); }
    finally { setTimeout(() => setPageLoading(false), Math.max(1200 - (Date.now() - t0), 0)); }
  }, [navigate]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  /* ── DIALOG HELPERS ── */
  const openAdd    = ()       => { setName(""); setDescription(""); setSelectedCourse(null); setDialogMode("add"); };
  const openEdit   = (c, e)   => { e.stopPropagation(); setSelectedCourse(c); setName(c.name); setDescription(c.description || ""); setDialogMode("edit"); };
  const openDelete = (c, e)   => { e.stopPropagation(); setSelectedCourse(c); setDialogMode("delete"); };
  const closeDialog = ()      => { setDialogMode(null); setSelectedCourse(null); setName(""); setDescription(""); };

  /* ── CRUD ── */
  const handleSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    const isEdit = dialogMode === "edit";
    try {
      const res = await fetch(
        isEdit ? `${process.env.REACT_APP_API_BASE_URL}/courses/${selectedCourse._id}` : `${process.env.REACT_APP_API_BASE_URL}/courses`,
        { method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ name, description }) }
      );
      if (res.status === 401 || res.status === 403) { logout(); navigate("/login"); return; }
      closeDialog(); fetchCourses();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!selectedCourse) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/courses/${selectedCourse._id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 401 || res.status === 403) { logout(); navigate("/login"); return; }
      closeDialog(); fetchCourses();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (pageLoading) return <HumanLoader loadingText="Preparing courses" successText="Let's start learning!" duration={1200} />;

  const filtered = courses.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="clp-page">

      {/* ── TOPBAR ── */}
      <div className="clp-topbar">
        <div>
          <h1 className="clp-title">My Courses</h1>
          <p className="clp-subtitle">Choose a course to explore its categories and problems</p>
        </div>
        <div className="clp-topbar-right">
          <div className="clp-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input placeholder="Search courses…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {isManagement && (
            <button className="clp-add-btn" onClick={openAdd}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Course
            </button>
          )}
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div className="clp-stats">
        {[
          { val: courses.length,  lbl: "Total Courses" },
          { val: filtered.length, lbl: "Showing"       },
          { val: role,            lbl: "Your Role", cap: true },
        ].map((s, i) => (
          <div key={i} className="clp-stat-item">
            <span className={`clp-stat-val ${s.cap ? "clp-stat-role" : ""}`}>{s.val}</span>
            <span className="clp-stat-lbl">{s.lbl}</span>
            {i < 2 && <div className="clp-stat-div" />}
          </div>
        ))}
      </div>

      {/* ── GRID ── */}
      {filtered.length === 0 ? (
        <div className="clp-empty">
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
          </svg>
          <p className="clp-empty-title">{search ? "No courses match your search" : "No Courses Available"}</p>
          <p className="clp-empty-sub">
            {search ? "Try a different keyword." : "You are not assigned to any courses yet. Contact your administrator for access."}
          </p>
        </div>
      ) : (
        <div className="clp-grid">
          {filtered.map((course, idx) => {
            const c = CARD_COLORS[idx % CARD_COLORS.length];
            return (
              <div key={course._id} className="clp-card">
                <div className="clp-card-accent" style={{ background: c.accent }} />

                <div className="clp-card-head">
                  <div className="clp-card-icon" style={{ background: c.bg, color: c.text }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                    </svg>
                  </div>
                  {isManagement && (
                    <div className="clp-card-actions">
                      <button className="clp-action-btn clp-edit" onClick={e => openEdit(course, e)}>
                        Edit
                      </button>
                      <button className="clp-action-btn clp-delete" onClick={e => openDelete(course, e)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                <h3 className="clp-card-title">{course.name}</h3>
                <p className="clp-card-desc">{course.description || "No description provided for this course."}</p>

                <button className="clp-card-btn" style={{ "--btn-color": c.accent }} onClick={() => navigate(`/courses/${course._id}/categories`)}>
                  View Categories
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            );
          })}

          {filtered.length % 3 !== 0 &&
            Array.from({ length: 3 - (filtered.length % 3) }).map((_, i) => (
              <div key={`ghost-${i}`} className="clp-card clp-ghost">
                <div className="clp-ghost-accent" />
                <div className="clp-ghost-icon" />
                <div className="clp-ghost-title" />
                <div className="clp-ghost-line" />
                <div className="clp-ghost-line clp-ghost-short" />
                <div className="clp-ghost-btn" />
              </div>
            ))}
        </div>
      )}

      {/* ── DIALOG ── */}
      {dialogMode && (
        <div className="clp-overlay" onClick={closeDialog}>
          <div className="clp-dialog" onClick={e => e.stopPropagation()}>

            {dialogMode === "delete" ? (
              <>
                <div className="clp-dialog-icon clp-icon-danger">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                  </svg>
                </div>
                <h3 className="clp-dialog-title">Delete Course?</h3>
                <p className="clp-dialog-sub">
                  You are about to permanently delete <strong>"{selectedCourse?.name}"</strong>. This cannot be undone.
                </p>
                <div className="clp-dialog-actions">
                  <button className="clp-btn-cancel" onClick={closeDialog}>Cancel</button>
                  <button className="clp-btn-danger" onClick={handleDelete} disabled={loading}>
                    {loading ? "Deleting…" : "Delete Course"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="clp-dialog-icon clp-icon-primary">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                  </svg>
                </div>
                <h3 className="clp-dialog-title">{dialogMode === "edit" ? "Edit Course" : "Add New Course"}</h3>
                <p className="clp-dialog-sub">{dialogMode === "edit" ? "Update the course details below." : "Fill in the details to create a new course."}</p>

                <div className="clp-form-group">
                  <label>Course Name</label>
                  <input className="clp-input" placeholder="e.g. Data Structures & Algorithms" value={name} onChange={e => setName(e.target.value)} autoFocus />
                </div>
                <div className="clp-form-group">
                  <label>Description <span className="clp-optional">(optional)</span></label>
                  <textarea className="clp-textarea" placeholder="Brief outline of the course objectives…" value={description} onChange={e => setDescription(e.target.value)} rows={4} />
                </div>

                <div className="clp-dialog-actions">
                  <button className="clp-btn-cancel" onClick={closeDialog}>Cancel</button>
                  <button className="clp-btn-primary" onClick={handleSave} disabled={loading || !name.trim()}>
                    {loading ? "Saving…" : dialogMode === "edit" ? "Save Changes" : "Create Course"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
